import type { QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { extractApiErrorMessage } from '#/utils/apiError'

export interface ICompanySwitchLocation {
  pathname: string
  search: string
  hash: string
}

/**
 * Static sections with more than one segment. Everything else that deep is a
 * record owned by the old company (users/{id} and its tabs) and is not rewritten.
 * `invoicing/lines` is the invoice-lines page, not a record.
 */
const MULTI_SEGMENT_SECTIONS: ReadonlySet<string> = new Set(['invoicing/lines'])

const UUID_VALUE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** `departmentId` and `userIds` name a record. `page` and `status` do not. */
function isResourceIdKey(key: string): boolean {
  return key === 'id' || key === 'ids' || key.endsWith('Id') || key.endsWith('Ids')
}

/**
 * Keep filters that still make sense for the next company. Drop keys that name
 * a record, and values that are ids, so the new page does not request company
 * A's department or user.
 */
function transferableSearch(search: string): string {
  if (search.length === 0) return ''

  const params = new URLSearchParams(search)
  const kept = new URLSearchParams()
  for (const [key, value] of params) {
    if (isResourceIdKey(key) || UUID_VALUE.test(value)) continue
    kept.append(key, value)
  }

  const next = kept.toString()
  return next.length === 0 ? '' : `?${next}`
}

function sectionPath(segments: readonly string[]): string | null {
  if (segments.length === 0) return ''
  if (segments.length === 1) return segments[0] ?? ''
  const path = segments.join('/')
  return MULTI_SEGMENT_SECTIONS.has(path) ? path : null
}

/**
 * Href for the same section under the new company.
 * Null when the page is not under the old company, or when it is a record
 * such as /users/{id}. The hash is kept. Search keys that point at a record are not.
 */
export function companySwitchHref(
  location: ICompanySwitchLocation,
  oldCompanyId: string,
  companyId: string,
): string | null {
  const prefix = `/companies/${oldCompanyId}`
  const { pathname, hash } = location
  const onCompany = pathname === prefix || pathname.startsWith(`${prefix}/`)
  if (!onCompany) return null

  const rest = pathname.slice(prefix.length).replace(/\/+$/, '')
  const segments = rest.split('/').filter((segment) => segment !== '')
  const section = sectionPath(segments)
  if (section === null) return null

  const nextPath = section.length === 0 ? `/companies/${companyId}` : `/companies/${companyId}/${section}`
  return `${nextPath}${transferableSearch(location.search)}${hash}`
}

/** Known refusals from PATCH /auth/me/selected-company. Anything else stays generic. */
export function companySwitchErrorMessage(error: unknown, t: (message: string) => string): string {
  const raw = extractApiErrorMessage(error)
  switch (raw) {
    case 'Access denied to this company':
      return t('Access denied to this company')
    case 'Company is disabled':
      return t('Company is disabled')
    case 'Company not found':
      return t('Company not found')
    case 'Invalid company ID format':
      return t('Invalid company ID format')
    default:
      return t('Could not switch company. Please try again.')
  }
}

const ACCESSIBLE_COMPANIES_PATH = '/auth/me/accessible-companies'

/** True for every cached query except the switcher's company list. */
export function isCompanyScopedQueryKey(queryKey: readonly unknown[]): boolean {
  return !(queryKey[0] === 'get' && queryKey[1] === ACCESSIBLE_COMPANIES_PATH)
}

function isCompanyScopedQuery(query: { queryKey: readonly unknown[] }): boolean {
  return isCompanyScopedQueryKey(query.queryKey)
}

/**
 * Drop cached data for the previous company. The switcher's company list stays.
 * Active queries are removed, not invalidated: invalidating them refetches the
 * page that is still on screen and blocks navigation until those requests settle.
 * The page that mounts after the switch fetches for the new company.
 */
export async function resetCompanyScopedQueries(queryClient: QueryClient): Promise<void> {
  await queryClient.cancelQueries({ predicate: isCompanyScopedQuery })
  queryClient.removeQueries({ predicate: isCompanyScopedQuery })
}

interface ISwitchCompanyArg {
  body: { companyId: string }
}

export interface IRunCompanySwitch {
  companyId: string
  oldCompanyId: string | undefined
  location: ICompanySwitchLocation
  fallback: 'company' | 'day-cards'
  switchCompany: (args: ISwitchCompanyArg) => Promise<unknown>
  refreshUser: () => Promise<{ companyId?: string }>
  navigate: (
    options:
      | { href: string }
      | { to: '/companies/$companyId'; params: { companyId: string } }
      | { to: '/companies/$companyId/day-cards'; params: { companyId: string } },
  ) => Promise<unknown>
  queryClient: QueryClient
  t: (message: string) => string
}

async function forgetPreviousCompany(queryClient: QueryClient): Promise<void> {
  try {
    await resetCompanyScopedQueries(queryClient)
  } catch (error: unknown) {
    console.error('[company-switch] cache reset failed after the company was stored', error)
  }
}

function fallbackNavigation(
  input: IRunCompanySwitch,
):
  | { to: '/companies/$companyId'; params: { companyId: string } }
  | { to: '/companies/$companyId/day-cards'; params: { companyId: string } } {
  const params = { companyId: input.companyId }
  if (input.fallback === 'day-cards') return { to: '/companies/$companyId/day-cards', params }

  return { to: '/companies/$companyId', params }
}

async function openSwitchedCompany(input: IRunCompanySwitch): Promise<void> {
  const href =
    input.oldCompanyId === undefined ? null : companySwitchHref(input.location, input.oldCompanyId, input.companyId)

  if (href) {
    try {
      await input.navigate({ href })
      return
    } catch (error: unknown) {
      console.error('[company-switch] section navigation failed; opening the fallback page', error)
    }
  }

  await input.navigate(fallbackNavigation(input))
}

/** PATCH the previous company. The profile is reloaded only when the caller asks. */
async function restoreSelectedCompany(input: IRunCompanySwitch, reloadProfile: boolean): Promise<boolean> {
  if (input.oldCompanyId === undefined) return false

  try {
    await input.switchCompany({ body: { companyId: input.oldCompanyId } })
  } catch (error: unknown) {
    console.error('[company-switch] rollback of the selected company failed', error)
    return false
  }

  if (!reloadProfile) return true

  try {
    const restored = await input.refreshUser()
    if (restored.companyId !== input.oldCompanyId) return false
    await forgetPreviousCompany(input.queryClient)
    return true
  } catch (error: unknown) {
    console.error('[company-switch] profile reload failed while restoring the previous company', error)
    return false
  }
}

/**
 * Store the company, reload the user, drop the previous company's cache, then
 * open the same section. A failed store or reload stays on this page. A failed
 * navigation rolls the selection back so the profile and the URL still match.
 */
export async function runCompanySwitch(input: IRunCompanySwitch): Promise<void> {
  const { companyId, t } = input

  try {
    await input.switchCompany({ body: { companyId } })
  } catch (error: unknown) {
    console.error('[company-switch] selected company was not stored', error)
    toast.error(companySwitchErrorMessage(error, t))
    return
  }

  try {
    const freshUser = await input.refreshUser()
    // A silent or stale profile must not navigate: the switcher would
    // still show the old company and a re-pick would send another PATCH.
    if (freshUser.companyId !== companyId) throw new Error('Selected company did not apply')
  } catch (error: unknown) {
    console.error('[company-switch] profile reload failed after the company was stored', error)
    const rolledBack = await restoreSelectedCompany(input, false)
    toast.error(
      rolledBack
        ? t('Could not switch company. Please try again.')
        : t('The company was switched, but your profile could not be reloaded. Refresh the page.'),
    )
    return
  }

  // Drop the previous company's cache before the URL changes, without refetching
  // the page that is still open. The company list stays.
  await forgetPreviousCompany(input.queryClient)

  try {
    await openSwitchedCompany(input)
  } catch (error: unknown) {
    console.error('[company-switch] navigation failed after the company was stored', error)
    const rolledBack = await restoreSelectedCompany(input, true)
    toast.error(
      rolledBack
        ? t('Could not switch company. Please try again.')
        : t('The company was switched, but the page could not be opened. Refresh the page.'),
    )
  }
}
